import { useRef, useId, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { MenuArrow, MenuBar, MenuColumn, MenuDivider, MenuItem, MenuPanel, MenuTrigger } from './parts'
import { MenuDrawer } from './parts/MenuDrawer'
import { MenuResponsive } from './parts/MenuResponsive'
import { useMenuState } from './hooks/useMenuState'
import { useOutsideClick } from './hooks/useOutsideClick'
import { useEscapeKey } from './hooks/useEscapeKey'
import { cn } from '../_utils/cn'

const menuItemShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  nombre: PropTypes.string.isRequired,
  orden: PropTypes.number,
  url: PropTypes.string,
  children: PropTypes.array,
})

/**
 * Componente Menu compound.
 *
 * Uso simple (data-driven):
 * ```jsx
 * <Menu.Bar items={items} onSelect={(node) => navigate(node.url)} />
 * ```
 *
 * Uso compound (escape hatch):
 * ```jsx
 * <Menu.Root id="mi-contrato" isOpen={isOpen} onOpenChange={setOpen}>
 *   <Menu.Trigger label="Mi contrato" />
 *   <Menu.Panel>
 *     <Menu.Column>
 *       <Menu.Item isSelected={active === 1} hasChildren onExpand={() => handle(1)}>Trabajadores</Menu.Item>
 *     </Menu.Column>
 *   </Menu.Panel>
 * </Menu.Root>
 * ```
 *
 * @param {MenuRootProps & MenuBarComponentProps} props
 */
export const Menu = ({ id, isOpen: isOpenProp, onOpenChange, dataTestId, className, children }) => {
  const internalId = useId()
  const rootRef = useRef(null)

  const {
    openId,
    activeLevel2Id,
    activeLevel3,
    isOpen: internalIsOpen,
    open,
    close,
    selectLevel2,
    clearLevel3,
  } = useMenuState()

  const isControlled = isOpenProp !== undefined
  const isOpen = isControlled ? isOpenProp : internalIsOpen

  const handleOpenChange = (nextOpen) => {
    if (isControlled) {
      onOpenChange?.(nextOpen)
      return
    }

    if (nextOpen) {
      open(id ?? internalId)
    } else {
      close()
    }
  }

  const handleToggle = () => {
    handleOpenChange(!isOpen)
  }

  const handleClose = () => {
    handleOpenChange(false)
  }

  useOutsideClick(rootRef, handleClose)
  useEscapeKey(handleClose)

  if (children) {
    return (
      <div ref={rootRef} className={className} data-testid={dataTestId}>
        {typeof children === 'function'
          ? children({
              isOpen,
              open: handleToggle,
              close: handleClose,
              toggle: handleToggle,
              openId,
              activeLevel2Id,
              activeLevel3,
              selectLevel2,
              clearLevel3,
            })
          : children}
      </div>
    )
  }

  return null
}

/**
 * @param {MenuBarComponentProps} props
 */
Menu.Bar = function MenuBarComponent({ items, onSelect, selectedId, defaultSelectedId, dataTestId, className }) {
  const rootRef = useRef(null)

  const {
    openId,
    activeLevel2Id,
    activeLevel3,
    isOpen: internalIsOpen,
    open,
    close,
    selectLevel2,
    clearLevel3,
    selectedItemId,
    selectItem,
  } = useMenuState()

  const isSelectedControlled = selectedId !== undefined
  const [internalSelectedId, setInternalSelectedId] = useState(defaultSelectedId ?? null)
  const selectedLevel1Id = isSelectedControlled ? selectedId : internalSelectedId

  const setSelectedLevel1Id = useCallback(
    /** @param {number|string|null} id */ (id) => {
      if (!isSelectedControlled) {
        setInternalSelectedId(id)
      }
    },
    [isSelectedControlled]
  )

  const handleClose = () => close()
  useOutsideClick(rootRef, handleClose)
  useEscapeKey(handleClose)

  const sortedItems = [...items].sort((a, b) => a.orden - b.orden)

  const currentOpenItem = sortedItems.find((item) => item.id === openId)
  const level2Items = currentOpenItem?.children ?? []
  const sortedLevel2 = [...level2Items].sort((a, b) => a.orden - b.orden)
  const activeLevel3Items = [...activeLevel3].sort((a, b) => a.orden - b.orden)

  const handleItemSelect = (node) => {
    if (node.children && node.children.length > 0) {
      selectLevel2(node.id, node.children)
    } else {
      selectItem(node.id)
      onSelect?.(node)
      close()
    }
  }

  const handleLevel3Select = (node) => {
    selectItem(node.id)
    onSelect?.(node)
    close()
  }

  return (
    <div ref={rootRef} className={cn('relative z-40', className)} data-testid={dataTestId}>
      {internalIsOpen && (
        <div
          aria-hidden="true"
          className="absolute left-0 right-0 top-full z-40 h-screen"
          style={{ backgroundColor: 'rgba(38, 38, 38, 0.3)' }}
          onPointerDown={close}
        />
      )}
      <MenuBar className="relative z-50">
        {sortedItems.map((item) => {
          const itemPanelId = `menu-panel-${item.id}`
          const itemTriggerId = `menu-trigger-${item.id}`
          const isItemOpen = openId === item.id

          return (
            <div key={item.id} className="relative h-full">
              <MenuTrigger
                id={itemTriggerId}
                label={item.nombre}
                isOpen={isItemOpen}
                isSelected={selectedLevel1Id === item.id}
                panelId={itemPanelId}
                onClick={() => {
                  if (openId === item.id) {
                    close()
                    return
                  }

                  setSelectedLevel1Id(item.id)
                  open(item.id)
                }}
                dataTestId={`${dataTestId}-trigger-${item.id}`}
              />
              {selectedLevel1Id === item.id && (
                <span
                  className="absolute bottom-0 left-0 right-0 h-0.5 mt-1 rounded bg-primary-10"
                  aria-hidden="true"
                  data-testid={`${dataTestId}-trigger-${item.id}-selected-indicator`}
                />
              )}
              {isItemOpen && (
                <MenuArrow
                  isOpen={isItemOpen}
                  className="absolute left-1/2 -translate-x-1/2"
                  style={{
                    top: selectedLevel1Id === item.id ? 'calc(100% - 5px)' : 'calc(100% + 16px)',
                    zIndex: 60,
                  }}
                />
              )}
              <MenuPanel
                id={itemPanelId}
                isOpen={isItemOpen}
                triggerId={itemTriggerId}
                dataTestId={`${dataTestId}-panel-${item.id}`}
                className={cn(
                  '!absolute left-0',
                  selectedLevel1Id === item.id ? 'top-[calc(100%-5px)]' : 'top-full mt-[16px]'
                )}
                showArrow={false}
              >
                <div className="flex items-stretch gap-6" onMouseLeave={clearLevel3}>
                  <MenuColumn>
                    {sortedLevel2.map((level2Item) => {
                      const hasLevel3 = level2Item.children && level2Item.children.length > 0
                      const isLevel2Expanded = activeLevel2Id === level2Item.id

                      return (
                        <MenuItem
                          key={level2Item.id}
                          id={`menu-item-${level2Item.id}`}
                          isSelected={selectedItemId === level2Item.id}
                          isExpanded={isLevel2Expanded}
                          hasChildren={hasLevel3}
                          onExpand={() => selectLevel2(level2Item.id, level2Item.children)}
                          onMouseEnter={() => {
                            if (hasLevel3) {
                              selectLevel2(level2Item.id, level2Item.children)
                            } else {
                              clearLevel3()
                            }
                          }}
                          onSelect={() => handleItemSelect(level2Item)}
                          dataTestId={`${dataTestId}-item-${level2Item.id}`}
                        >
                          {level2Item.nombre}
                        </MenuItem>
                      )
                    })}
                  </MenuColumn>

                  {activeLevel3Items.length > 0 && (
                    <>
                      <MenuDivider />
                      <MenuColumn>
                        {activeLevel3Items.map((level3Item) => (
                          <MenuItem
                            key={level3Item.id}
                            id={`menu-item-${level3Item.id}`}
                            hasChildren={false}
                            isSelected={selectedItemId === level3Item.id}
                            onSelect={() => handleLevel3Select(level3Item)}
                            dataTestId={`${dataTestId}-item-${level3Item.id}`}
                          >
                            {level3Item.nombre}
                          </MenuItem>
                        ))}
                      </MenuColumn>
                    </>
                  )}
                </div>
              </MenuPanel>
            </div>
          )
        })}
      </MenuBar>
    </div>
  )
}

Menu.Root = Menu
Menu.Trigger = MenuTrigger
Menu.Panel = MenuPanel
Menu.Column = MenuColumn
Menu.Divider = MenuDivider
Menu.Item = MenuItem
Menu.Drawer = MenuDrawer
Menu.Responsive = function MenuResponsiveComponent(props) {
  return <MenuResponsive {...props} DesktopComponent={Menu.Bar} />
}

Menu.displayName = 'Menu'

Menu.propTypes = {
  id: PropTypes.string,
  isOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  dataTestId: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
}

Menu.Bar.propTypes = {
  items: PropTypes.arrayOf(menuItemShape).isRequired,
  onSelect: PropTypes.func,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultSelectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dataTestId: PropTypes.string,
  className: PropTypes.string,
}

Menu.Responsive.propTypes = {
  items: PropTypes.arrayOf(menuItemShape).isRequired,
  onSelect: PropTypes.func,
  mobileOpen: PropTypes.bool,
  mobileInitialFooterItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onMobileOpenChange: PropTypes.func,
  footerItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      onSelect: PropTypes.func,
      dividerBefore: PropTypes.bool,
      showChevron: PropTypes.bool,
      renderContent: PropTypes.func,
    })
  ),
  mode: PropTypes.oneOf(['auto', 'desktop', 'mobile']),
  dataTestId: PropTypes.string,
  className: PropTypes.string,
}

export default Menu
